import React, {
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from "recharts";

import {
  FaUsers,
  FaTruck,
  FaBoxes,
  FaShoppingCart,
  FaArrowCircleLeft,
  FaUserCog,
  FaChartLine,
  FaThLarge,
  FaSyncAlt,
  FaArrowUp,
  FaShoppingBag,
  FaMoneyBillWave,
  FaFileInvoiceDollar
} from "react-icons/fa";

import { MdInventory } from "react-icons/md";

import {
  FiAlertCircle,
  FiTrendingUp,
  FiActivity,
  FiPackage
} from "react-icons/fi";

import { useAuth } from "../module_roots/AuthContext";

/* ============================================================
   CONSTANTS
============================================================ */

const SALES_YELLOW = "#f5b91e";
const PURCHASE_BLUE = "#3b82f6";
const SUCCESS_GREEN = "#10b981";
const DANGER_RED = "#ef4444";
const DASHBOARD_ANALYTICS_URL =
  "https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_dashboard_analytics.php";

const fetchDashboardAnalytics = async (shopId, filters, token) => {
  const response = await fetch(DASHBOARD_ANALYTICS_URL, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      shop_id: shopId,
      start_date: filters.startDate,
      end_date: filters.endDate
    })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to load dashboard analytics");
  }
  return data;
};


/* ============================================================
   THEME COLOR
   Reads current app theme from CSS + localStorage.

   IMPORTANT:
   CSS variable --primary-color is still preferred when your
   global theme system has already applied the selected theme.
============================================================ */

const isValidCSSColor = (value) => {
  if (!value || typeof value !== "string") return false;

  if (typeof document === "undefined") return true;

  const test = document.createElement("span");
  test.style.color = "";
  test.style.color = value.trim();

  return test.style.color !== "";
};


const extractColorFromThemeValue = (rawValue) => {
  if (!rawValue) return null;

  const trimmed = String(rawValue).trim();

  /* Direct stored color */
  if (isValidCSSColor(trimmed)) {
    /*
      Ignore values that are theme mode names rather than colors.
    */
    const lower = trimmed.toLowerCase();

    if (
      lower !== "dark" &&
      lower !== "light" &&
      lower !== "system" &&
      lower !== "default"
    ) {
      return trimmed;
    }
  }

  /* JSON theme object */
  try {
    const parsed = JSON.parse(trimmed);

    if (parsed && typeof parsed === "object") {
      const possibleColors = [
        parsed.primaryColor,
        parsed.primary_color,
        parsed.primary,
        parsed.accentColor,
        parsed.accent_color,
        parsed.accent,
        parsed.themeColor,
        parsed.theme_color,
        parsed.color
      ];

      for (const color of possibleColors) {
        if (
          typeof color === "string" &&
          isValidCSSColor(color)
        ) {
          return color.trim();
        }
      }
    }
  } catch (error) {
    /* Not JSON — completely fine */
  }

  return null;
};


const getDashboardThemeColor = () => {
  if (typeof window === "undefined") {
    return "#10b981";
  }

  /*
   * 1. Check common localStorage theme/color keys.
   *
   * Add your exact key to this array if your application
   * uses a different name.
   */
  const storageKeys = [
    "ourzest_theme_color",
    "ourzest_primary_color",
    "ourzest_theme",
    "themeColor",
    "primaryColor",
    "primary_color",
    "accentColor",
    "accent_color",
    "theme"
  ];

  for (const key of storageKeys) {
    const stored = localStorage.getItem(key);
    const extracted = extractColorFromThemeValue(stored);

    if (extracted) {
      return extracted;
    }
  }

  /*
   * 2. Read the actual theme CSS variable used by the app.
   */
  const rootStyles = getComputedStyle(
    document.documentElement
  );

  const cssPrimary = rootStyles
    .getPropertyValue("--primary-color")
    .trim();

  if (cssPrimary && isValidCSSColor(cssPrimary)) {
    return cssPrimary;
  }

  /*
   * 3. Check body in case variables are scoped there.
   */
  const bodyStyles = getComputedStyle(document.body);

  const bodyPrimary = bodyStyles
    .getPropertyValue("--primary-color")
    .trim();

  if (
    bodyPrimary &&
    isValidCSSColor(bodyPrimary)
  ) {
    return bodyPrimary;
  }

  return "#10b981";
};


/* ============================================================
   PKR FORMATTERS
============================================================ */

const formatPKR = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "₨0";
  }

  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
    .format(num)
    .replace("PKR", "₨");
};


const formatCompactPKR = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "₨0";
  }

  if (Math.abs(num) >= 10000000) {
    return `₨${(num / 10000000).toFixed(1)}Cr`;
  }

  if (Math.abs(num) >= 100000) {
    return `₨${(num / 100000).toFixed(1)}L`;
  }

  if (Math.abs(num) >= 1000) {
    return `₨${(num / 1000).toFixed(1)}K`;
  }

  return `₨${Math.round(num)}`;
};


const formatNodeValue = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "0";
  }

  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(1)}Cr`;
  }

  if (num >= 100000) {
    return `${(num / 100000).toFixed(1)}L`;
  }

  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }

  return Math.round(num).toLocaleString("en-PK");
};


/* ============================================================
   TRANSLATIONS
============================================================ */

const createDashboardTranslator = () => {
  const defaultTexts = {
    todaysSales: "Today's Sales",
    totalCustomers: "Total Customers",
    suppliers: "Suppliers",
    products: "Products",
    inventoryValue: "Inventory Value",
    lowStockAlert: "Low Stock Alert",
    totalReturns: "Total Returns",
    totalEmployees: "Total Employees",

    cardsView: "Cards",
    chartsView: "Charts",

    revenueTrend: "Sales & Revenue Trend",
    inventoryTrend: "Stock & Inventory Health",
    businessComparison: "Sales vs Purchases",

    overview: "Overview Analytics",
    overviewSub: "Live business intelligence & performance",

    noData: "No analytics available"
  };

  const getTranslations = async () => {
    return defaultTexts;
  };

  return {
    getTranslations
  };
};


const dashboardTranslator =
  createDashboardTranslator();


/* ============================================================
   COUNT-UP ANIMATION
============================================================ */

const useCountUp = (
  rawValue,
  duration = 550
) => {
  const [displayValue, setDisplayValue] =
    useState(Number(rawValue) || 0);

  useEffect(() => {
    const endValue = Number(rawValue) || 0;
    const startValue = 0;

    let animationFrame;
    let startedAt = null;

    const animate = (time) => {
      if (!startedAt) {
        startedAt = time;
      }

      const elapsed =
        time - startedAt;

      const progress =
        Math.min(
          elapsed / duration,
          1
        );

      const eased =
        1 -
        Math.pow(
          1 - progress,
          3
        );

      setDisplayValue(
        startValue +
          (endValue - startValue) *
            eased
      );

      if (progress < 1) {
        animationFrame =
          requestAnimationFrame(
            animate
          );
      }
    };

    animationFrame =
      requestAnimationFrame(
        animate
      );

    return () =>
      cancelAnimationFrame(
        animationFrame
      );
  }, [rawValue, duration]);

  return displayValue;
};


/* ============================================================
   MAIN SALES CHART NODE

   Sharp point + vertical leader + number
============================================================ */

const SalesValueNode = ({
  cx,
  cy,
  value,
  stroke
}) => {
  if (
    cx === undefined ||
    cy === undefined
  ) {
    return null;
  }

  return (
    <g>
      {/* Node */}
      <circle
        cx={cx}
        cy={cy}
        r="3.6"
        fill={stroke}
        stroke="var(--oz-surface)"
        strokeWidth="2"
      />

      {/* Vertical leader */}
      <line
        x1={cx}
        y1={cy - 5}
        x2={cx}
        y2={cy - 19}
        stroke={stroke}
        strokeWidth="1"
        opacity="0.72"
      />

      {/* Tiny end node */}
      <circle
        cx={cx}
        cy={cy - 19}
        r="1.6"
        fill={stroke}
      />

      {/* Value */}
      <text
        x={cx}
        y={cy - 25}
        textAnchor="middle"
        className="oz-main-node-value"
      >
        {formatNodeValue(value)}
      </text>
    </g>
  );
};


/* ============================================================
   SALES VS PURCHASES NODE

   Sales labels go UP.
   Purchase labels go DOWN.
============================================================ */

const ComparisonValueNode = ({
  cx,
  cy,
  value,
  stroke,
  direction = "up"
}) => {
  if (
    cx === undefined ||
    cy === undefined
  ) {
    return null;
  }

  const isUp =
    direction === "up";

  const leaderY =
    isUp
      ? cy - 14
      : cy + 14;

  const textY =
    isUp
      ? cy - 19
      : cy + 25;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r="3"
        fill={stroke}
        stroke="var(--oz-surface)"
        strokeWidth="1.7"
      />

      <line
        x1={cx}
        y1={
          isUp
            ? cy - 4
            : cy + 4
        }
        x2={cx}
        y2={leaderY}
        stroke={stroke}
        strokeWidth="0.9"
        opacity="0.72"
      />

      <circle
        cx={cx}
        cy={leaderY}
        r="1.3"
        fill={stroke}
      />

      <text
        x={cx}
        y={textY}
        textAnchor="middle"
        className={
          isUp
            ? "oz-compare-node-value sales"
            : "oz-compare-node-value purchase"
        }
      >
        {formatNodeValue(value)}
      </text>
    </g>
  );
};


/* ============================================================
   CLEAN SUMMARY CARD
   NO SPARKLINES / NO LINE GRAPH
============================================================ */


const SummaryStatCard = ({
  title,
  value,
  icon: Icon,
  subtitle,
  themeColor,
  isCurrency = false,
  trend,
  danger = false,
  onClick
}) => {
  const numericValue = Number(value) || 0;

  const animatedValue = useCountUp(numericValue);

  const displayValue = isCurrency
    ? formatPKR(animatedValue)
    : Math.round(animatedValue).toLocaleString("en-PK");

  return (
    <button
      type="button"
      className={`oz-summary-card ${
        danger ? "oz-summary-danger" : ""
      }`}
      onClick={onClick}
      style={{
        "--summary-accent": danger
          ? DANGER_RED
          : themeColor
      }}
    >
      {/* =====================================================
          TOP ROW
          ICON + TITLE LEFT
          TREND RIGHT
      ===================================================== */}

      <div className="oz-summary-card-top">

        <div className="oz-summary-heading">
          <span className="oz-summary-icon">
            <Icon />
          </span>

          <span className="oz-summary-title">
            {title}
          </span>
        </div>

        {trend && (
          <span
            className={`oz-summary-trend ${
              danger ? "danger" : ""
            }`}
          >
            {!danger && <FaArrowUp />}

            {trend}
          </span>
        )}

      </div>

      {/* =====================================================
          VALUE + SUBTITLE
      ===================================================== */}

      <div className="oz-summary-content">

        <strong className="oz-summary-value">
          {displayValue}
        </strong>

        {subtitle && (
          <span className="oz-summary-subtitle">
            {subtitle}
          </span>
        )}

      </div>
    </button>
  );
};

/* ============================================================
   REVENUE TOOLTIP
============================================================ */

const RevenueTooltip = ({
  active,
  payload,
  label
}) => {
  if (
    !active ||
    !payload?.length
  ) {
    return null;
  }

  return (
    <div className="oz-chart-tooltip">
      <span className="oz-tooltip-title">
        {label}
      </span>

      {payload.map(
        (entry) => (
          <div
            className="oz-tooltip-row"
            key={`${entry.dataKey}-${entry.value}`}
          >
            <span
              className="oz-tooltip-dot"
              style={{
                background:
                  entry.color ||
                  entry.stroke
              }}
            />

            <span className="oz-tooltip-name">
              {entry.name}
            </span>

            <strong>
              {formatPKR(
                entry.value
              )}
            </strong>
          </div>
        )
      )}
    </div>
  );
};


/* ============================================================
   MAIN COMPONENT
============================================================ */

const CosmeticsDashboardHome = ({
  onNavigate,
  setFormContent,
  onBreadcrumbUpdate
}) => {
  const {
    user,
    currentShop,
    loading: authLoading
  } = useAuth();

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    currentLanguage
  ] = useState(
    () =>
      localStorage.getItem(
        "ourzest_language"
      ) || "en"
  );

  const [
    translations,
    setTranslations
  ] = useState({});

  const [
    viewMode,
    setViewMode
  ] = useState("charts");

  /* =========================================================
     THEME COLOR STATE
  ========================================================= */

  const [
    themeColor,
    setThemeColor
  ] = useState(
    getDashboardThemeColor
  );


  /* =========================================================
     STATS
  ========================================================= */

  const [
    stats,
    setStats
  ] = useState({
    totalSuppliers: 0,
    totalCustomers: 0,
    totalProducts: 0,
    lowStockCount: 0,
    inventoryValue: 0,
    todaySales: 0,
    todaySalesCount: 0,
    totalSales: 0,
    totalPurchases: 0,
    totalPaid: 0,
    totalDues: 0
  });


  const [
    moduleStats,
    setModuleStats
  ] = useState({
    totalReturns: 0,
    totalReturnsAmount: 0,
    totalEmployees: 0,
    activeEmployees: 0
  });


  const [
    salesTrendData,
    setSalesTrendData
  ] = useState([]);


  /* =========================================================
     KEEP THEME SYNCED
  ========================================================= */

  useEffect(() => {
    const syncTheme =
      () => {
        const nextColor =
          getDashboardThemeColor();

        setThemeColor(
          (current) =>
            current !== nextColor
              ? nextColor
              : current
        );
      };

    /*
     * Changes made by another tab/window.
     */
    window.addEventListener(
      "storage",
      syncTheme
    );

    /*
     * Works if your application's Theme button emits
     * one of these custom events.
     */
    window.addEventListener(
      "themechange",
      syncTheme
    );

    window.addEventListener(
      "ourzest-theme-change",
      syncTheme
    );

    /*
     * Watches class/style changes when the app changes
     * theme through documentElement.
     */
    const observer =
      new MutationObserver(
        syncTheme
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "class",
          "style",
          "data-theme"
        ]
      }
    );

    /*
     * Native 'storage' does NOT fire in the same tab that
     * writes localStorage, therefore this very lightweight
     * sync guarantees the dashboard follows LocalStorage.
     */
    const interval =
      window.setInterval(
        syncTheme,
        700
      );

    syncTheme();

    return () => {
      window.removeEventListener(
        "storage",
        syncTheme
      );

      window.removeEventListener(
        "themechange",
        syncTheme
      );

      window.removeEventListener(
        "ourzest-theme-change",
        syncTheme
      );

      observer.disconnect();

      window.clearInterval(
        interval
      );
    };
  }, []);


  /* =========================================================
     NAVIGATION
  ========================================================= */

  const navigateToMenu =
    useCallback(
      (
        menuName,
        submenuName = null
      ) => {
        if (onNavigate) {
          onNavigate(
            menuName,
            submenuName
          );
        }
      },
      [onNavigate]
    );


  /* =========================================================
     TRANSLATION
  ========================================================= */

  useEffect(() => {
    const loadTranslations =
      async () => {
        const result =
          await dashboardTranslator.getTranslations(
            currentLanguage
          );

        setTranslations(
          result
        );
      };

    loadTranslations();
  }, [currentLanguage]);


  /* =========================================================
     DASHBOARD API
  ========================================================= */

  const loadDashboardData =
    useCallback(
      async () => {
        if (
          !user ||
          !currentShop
        ) {
          setLoading(false);
          return;
        }

        setLoading(true);

        try {
          const shopId =
            currentShop?.shop_id;

          const today =
            new Date()
              .toISOString()
              .split("T")[0];

          const monthStart = new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            1
          ).toISOString().split("T")[0];

          const defaultFilters = {
            startDate: monthStart,
            endDate: today,
            category: "all",
            paymentStatus:
              "all"
          };

          const dashboardData = await fetchDashboardAnalytics(
            shopId,
            defaultFilters,
            user?.token
          );


          /* ===============================================
             DASHBOARD STATS
          =============================================== */

          if (
            dashboardData &&
            dashboardData.stats
          ) {
            const dataStats =
              dashboardData.stats;

            const todaySales =
              Number(
                dataStats.today_sales
              ) || 0;

            const totalPurchases =
              Number(
                dataStats.total_purchases
              ) || 0;

            const totalProducts =
              Number(
                dataStats.total_products
              ) || 0;

            const lowStockCount =
              Number(
                dataStats.low_stock_count
              ) || 0;


            setStats({
              totalSuppliers:
                Number(
                  dataStats.total_suppliers
                ) || 0,

              totalCustomers:
                Number(
                  dataStats.total_customers
                ) || 0,

              totalProducts,

              lowStockCount,

              inventoryValue:
                Number(
                  dataStats.inventory_value
                ) || 0,

              todaySales,

              todaySalesCount:
                Number(
                  dataStats.today_sales_count
                ) || 0,

              totalSales:
                Number(dataStats.total_sales) || 0,

              totalPurchases,

              totalPaid:
                Number(dataStats.total_paid) || 0,

              totalDues:
                Number(dataStats.total_dues) || 0
            });


            /*
             * Use your real API hourly data here when available.
             *
             * This keeps your current synthetic display logic
             * while changing only the visualization.
             */

            const timeNodes = [
              "09:00",
              "10:00",
              "11:00",
              "12:00",
              "13:00",
              "14:00",
              "15:00",
              "16:00",
              "17:00",
              "18:00",
              "19:00",
              "20:00",
              "21:00",
              "22:00"
            ];


            const salesBase =
              todaySales > 0
                ? todaySales
                : 18000;


            const purchaseBase =
              totalPurchases >
              0
                ? totalPurchases
                : salesBase *
                  0.67;


            /*
             * Sharper zig-zag variation.
             * It intentionally creates rising/falling edges
             * instead of a soft looking curve.
             */

            const salesPattern = [
              0.33,
              0.46,
              0.41,
              0.65,
              0.52,
              0.79,
              0.61,
              0.93,
              0.71,
              1.05,
              0.83,
              1.16,
              0.97,
              1.28
            ];


            const purchasePattern = [
              0.27,
              0.38,
              0.30,
              0.49,
              0.40,
              0.61,
              0.48,
              0.72,
              0.57,
              0.81,
              0.66,
              0.91,
              0.74,
              1.00
            ];


            const trendData =
              timeNodes.map(
                (
                  time,
                  index
                ) => ({
                  time,

                  sales:
                    Math.round(
                      salesBase *
                        salesPattern[
                          index
                        ]
                    ),

                  purchases:
                    Math.round(
                      purchaseBase *
                        purchasePattern[
                          index
                        ]
                    )
                })
              );


            setSalesTrendData(
              (dashboardData.trend || []).map((point) => ({
                time: new Date(`${point.date}T00:00:00`).toLocaleDateString(
                  "en-PK",
                  { month: "short", day: "numeric" }
                ),
                sales: Number(point.sales) || 0,
                purchases: Number(point.purchases) || 0,
                paid: Number(point.paid) || 0,
                dues: Number(point.dues) || 0
              }))
            );
          }


          /* ===============================================
             RETURNS
          =============================================== */

          setModuleStats({
            totalReturns: Number(dashboardData.stats?.total_returns) || 0,
            totalReturnsAmount: Number(dashboardData.stats?.total_returns_amount) || 0,
            totalEmployees: Number(dashboardData.stats?.total_employees) || 0,
            activeEmployees: Number(dashboardData.stats?.active_employees) || 0
          });
        } catch (error) {
          console.error(
            "Error loading dashboard:",
            error
          );
        } finally {
          setLoading(false);
        }
      },
      [user, currentShop]
    );


  useEffect(() => {
    if (
      !authLoading &&
      user &&
      currentShop
    ) {
      loadDashboardData();
    }
  }, [
    authLoading,
    user,
    currentShop,
    loadDashboardData
  ]);


  /* =========================================================
     SPIDER / RADAR DATA
  ========================================================= */

  const inventoryRadarData =
    useMemo(() => {
      const products =
        Number(
          stats.totalProducts
        ) || 0;

      const lowStock =
        Number(
          stats.lowStockCount
        ) || 0;

      const suppliers =
        Number(
          stats.totalSuppliers
        ) || 0;

      const customers =
        Number(
          stats.totalCustomers
        ) || 0;

      const invoices =
        Number(
          stats.todaySalesCount
        ) || 0;

      const returns =
        Number(
          moduleStats.totalReturns
        ) || 0;


      const stockHealth =
        products > 0
          ? Math.max(
              10,
              Math.min(
                100,
                100 -
                  (lowStock /
                    products) *
                    100
              )
            )
          : 65;


      const productRange =
        Math.max(
          25,
          Math.min(
            100,
            products / 3
          )
        );


      const supplierNetwork =
        Math.max(
          20,
          Math.min(
            100,
            suppliers * 5
          )
        );


      const customerActivity =
        Math.max(
          20,
          Math.min(
            100,
            customers / 2
          )
        );


      const salesActivity =
        Math.max(
          20,
          Math.min(
            100,
            invoices * 4
          )
        );


      const returnHealth =
        Math.max(
          15,
          Math.min(
            100,
            100 -
              returns * 4
          )
        );


      return [
        {
          metric: "Stock",
          value:
            Math.round(
              stockHealth
            )
        },

        {
          metric: "Products",
          value:
            Math.round(
              productRange
            )
        },

        {
          metric: "Suppliers",
          value:
            Math.round(
              supplierNetwork
            )
        },

        {
          metric: "Customers",
          value:
            Math.round(
              customerActivity
            )
        },

        {
          metric: "Sales",
          value:
            Math.round(
              salesActivity
            )
        },

        {
          metric: "Returns",
          value:
            Math.round(
              returnHealth
            )
        }
      ];
    }, [
      stats,
      moduleStats
    ]);


  /* =========================================================
     PURCHASE RATIO
  ========================================================= */

  const purchaseRatio =
    stats.todaySales > 0
      ? (
          (stats.totalPurchases /
            stats.todaySales) *
          100
        ).toFixed(0)
      : 0;


  /* =========================================================
     LOADER
  ========================================================= */

  if (
    authLoading ||
    loading
  ) {
    return (
      <div className="oz-dashboard-loader">
        <div className="oz-dashboard-spinner" />

        <strong>
          Loading analytics
        </strong>

        <span>
          Preparing your business
          dashboard...
        </span>
      </div>
    );
  }


  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className="oz-analytics-dashboard"
      style={{
        "--dash-accent":
          themeColor
      }}
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <section className="oz-analytics-header">
        <div className="oz-analytics-heading">
          <div className="oz-heading-icon">
            <FiActivity />
          </div>

          <div>
            <h2>
              {translations.overview ||
                "Overview Analytics"}
            </h2>

            <p>
              {translations.overviewSub ||
                "Live business intelligence & performance"}
            </p>
          </div>
        </div>


        <div className="oz-analytics-actions">
          <div className="oz-view-selector">
            <button
              type="button"
              className={
                viewMode ===
                "charts"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode(
                  "charts"
                )
              }
            >
              <FaChartLine />

              <span>
                {translations.chartsView ||
                  "Charts"}
              </span>
            </button>


            <button
              type="button"
              className={
                viewMode ===
                "cards"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode(
                  "cards"
                )
              }
            >
              <FaThLarge />

              <span>
                {translations.cardsView ||
                  "Cards"}
              </span>
            </button>
          </div>


          <button
            type="button"
            className="oz-refresh-button"
            onClick={
              loadDashboardData
            }
            title="Refresh dashboard"
          >
            <FaSyncAlt />
          </button>
        </div>
      </section>


      {/* ===================================================
          CHART VIEW
      =================================================== */}

      {viewMode ===
        "charts" && (
        <div className="oz-chart-dashboard">

          {/* ===============================================
              MAIN SALES TREND
          =============================================== */}

          <section className="oz-chart-panel oz-main-sales-panel">
            <div className="oz-chart-panel-header">
              <div className="oz-chart-title-wrap">
                <span className="oz-chart-title-icon themed">
                  <FiTrendingUp />
                </span>

                <div>
                  <h3>
                    {translations.revenueTrend ||
                      "Sales & Revenue Trend"}
                  </h3>

                  <p>
                    Sharp node-by-node
                    sales movement
                  </p>
                </div>
              </div>


              <div className="oz-sales-summary">
                <div className="oz-sales-summary-value">
                  <strong>
                    {formatCompactPKR(
                      stats.todaySales
                    )}
                  </strong>

                  <span>
                    Today's revenue
                  </span>
                </div>


                <span className="oz-positive-chip">
                  <FaArrowUp />
                  Trending
                </span>
              </div>
            </div>


            <div className="oz-main-chart-area">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <LineChart
                  data={
                    salesTrendData
                  }
                  margin={{
                    top: 37,
                    right: 24,
                    left: -5,
                    bottom: 2
                  }}
                >
                  <CartesianGrid
                    stroke="var(--oz-chart-grid)"
                    strokeDasharray="1 3"
                    vertical
                  />


                  <XAxis
                    dataKey="time"
                    axisLine={{
                      stroke:
                        "var(--oz-axis)"
                    }}
                    tickLine={
                      false
                    }
                    tick={{
                      fill:
                        "var(--oz-muted)",
                      fontSize: 10
                    }}
                    dy={8}
                  />


                  <YAxis
                    axisLine={{
                      stroke:
                        "var(--oz-axis)"
                    }}
                    tickLine={
                      false
                    }
                    width={60}
                    tick={{
                      fill:
                        "var(--oz-muted)",
                      fontSize: 10
                    }}
                    tickFormatter={
                      formatCompactPKR
                    }
                  />


                  <RechartsTooltip
                    cursor={{
                      stroke:
                        themeColor,
                      strokeWidth:
                        1,
                      strokeDasharray:
                        "3 4",
                      opacity:
                        0.5
                    }}
                    content={
                      <RevenueTooltip />
                    }
                  />


                  <Line
                    type="linear"
                    dataKey="sales"
                    name="Sales"
                    stroke={
                      themeColor
                    }
                    strokeWidth={2.2}
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    isAnimationActive
                    animationDuration={
                      700
                    }
                    dot={(props) => (
                      <SalesValueNode
                        {...props}
                        stroke={
                          themeColor
                        }
                      />
                    )}
                    activeDot={{
                      r: 5.5,
                      fill:
                        themeColor,
                      stroke:
                        "var(--oz-surface)",
                      strokeWidth:
                        2.5
                    }}
                  />

                  <Line
                    type="linear"
                    dataKey="paid"
                    name="Paid"
                    stroke={SUCCESS_GREEN}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />

                  <Line
                    type="linear"
                    dataKey="dues"
                    name="Dues"
                    stroke={DANGER_RED}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>


          {/* ===============================================
              BOTTOM ROW
          =============================================== */}

          <div className="oz-secondary-chart-grid">

            {/* =============================================
                SALES VS PURCHASES
            ============================================= */}

            <section className="oz-chart-panel oz-comparison-panel">
              <div className="oz-chart-panel-header compact">
                <div className="oz-chart-title-wrap">
                  <span className="oz-chart-title-icon yellow">
                    <FaShoppingBag />
                  </span>

                  <div>
                    <h3>
                      {translations.businessComparison ||
                        "Sales vs Purchases"}
                    </h3>

                    <p>
                      Sharp comparison
                      movement
                    </p>
                  </div>
                </div>


                <div className="oz-chart-legend">
                  <span>
                    <i className="yellow" />
                    Sales
                  </span>

                  <span>
                    <i className="blue" />
                    Purchases
                  </span>

                  <span>
                    <i style={{ background: SUCCESS_GREEN }} />
                    Paid
                  </span>

                  <span>
                    <i style={{ background: DANGER_RED }} />
                    Dues
                  </span>
                </div>
              </div>


              <div className="oz-small-chart-area">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <LineChart
                    data={
                      salesTrendData
                    }
                    margin={{
                      top: 32,
                      right: 16,
                      left: -12,
                      bottom: 23
                    }}
                  >
                    <CartesianGrid
                      stroke="var(--oz-chart-grid)"
                      strokeDasharray="1 4"
                      vertical
                    />


                    <XAxis
                      dataKey="time"
                      axisLine={{
                        stroke:
                          "var(--oz-axis)"
                      }}
                      tickLine={
                        false
                      }
                      interval={2}
                      tick={{
                        fill:
                          "var(--oz-muted)",
                        fontSize:
                          9
                      }}
                      dy={7}
                    />


                    <YAxis
                      axisLine={{
                        stroke:
                          "var(--oz-axis)"
                      }}
                      tickLine={
                        false
                      }
                      width={53}
                      tick={{
                        fill:
                          "var(--oz-muted)",
                        fontSize:
                          9
                      }}
                      tickFormatter={
                        formatCompactPKR
                      }
                    />


                    <RechartsTooltip
                      content={
                        <RevenueTooltip />
                      }
                    />


                    {/* SALES — YELLOW */}

                    <Line
                      type="linear"
                      dataKey="sales"
                      name="Sales"
                      stroke={
                        SALES_YELLOW
                      }
                      strokeWidth={
                        2.15
                      }
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      animationDuration={
                        650
                      }
                      dot={(props) => (
                        <ComparisonValueNode
                          {...props}
                          stroke={
                            SALES_YELLOW
                          }
                          direction="up"
                        />
                      )}
                      activeDot={{
                        r: 5,
                        fill:
                          SALES_YELLOW,
                        stroke:
                          "var(--oz-surface)",
                        strokeWidth:
                          2
                      }}
                    />


                    {/* PURCHASES — BLUE */}

                    <Line
                      type="linear"
                      dataKey="purchases"
                      name="Purchases"
                      stroke={
                        PURCHASE_BLUE
                      }
                      strokeWidth={
                        2.15
                      }
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      animationDuration={
                        800
                      }
                      dot={(props) => (
                        <ComparisonValueNode
                          {...props}
                          stroke={
                            PURCHASE_BLUE
                          }
                          direction="down"
                        />
                      )}
                    activeDot={{
                      r: 5,
                      fill:
                        PURCHASE_BLUE,
                        stroke:
                          "var(--oz-surface)",
                      strokeWidth:
                        2
                    }}
                  />

                  <Line
                    type="linear"
                    dataKey="paid"
                    name="Paid"
                    stroke={SUCCESS_GREEN}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />

                  <Line
                    type="linear"
                    dataKey="dues"
                    name="Dues"
                    stroke={DANGER_RED}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />
                </LineChart>
                </ResponsiveContainer>
              </div>


              <div className="oz-chart-bottom-metrics">
                <div>
                  <span>
                    Sales
                  </span>

                  <strong className="yellow-text">
                    {formatCompactPKR(
                      stats.todaySales
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    Purchases
                  </span>

                  <strong className="blue-text">
                    {formatCompactPKR(
                      stats.totalPurchases
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    Purchase Ratio
                  </span>

                  <strong>
                    {purchaseRatio}%
                  </strong>
                </div>

                <div>
                  <span>Paid</span>
                  <strong style={{ color: SUCCESS_GREEN }}>
                    {formatCompactPKR(stats.totalPaid)}
                  </strong>
                </div>

                <div>
                  <span>Dues</span>
                  <strong style={{ color: DANGER_RED }}>
                    {formatCompactPKR(stats.totalDues)}
                  </strong>
                </div>
              </div>
            </section>


            {/* =============================================
                STOCK / INVENTORY SPIDER
            ============================================= */}

            <section className="oz-chart-panel oz-radar-panel">
              <div className="oz-chart-panel-header compact">
                <div className="oz-chart-title-wrap">
                  <span className="oz-chart-title-icon themed">
                    <MdInventory />
                  </span>

                  <div>
                    <h3>
                      {translations.inventoryTrend ||
                        "Stock & Inventory Health"}
                    </h3>

                    <p>
                      Inventory operational
                      health
                    </p>
                  </div>
                </div>


                <span className="oz-health-badge">
                  <span />
                  Healthy
                </span>
              </div>


              <div className="oz-radar-content">
                <div className="oz-radar-chart-area">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <RadarChart
                      data={
                        inventoryRadarData
                      }
                      outerRadius="73%"
                    >
                      <PolarGrid
                        stroke="var(--oz-chart-grid-strong)"
                      />


                      <PolarAngleAxis
                        dataKey="metric"
                        tick={{
                          fill:
                            "var(--oz-muted)",
                          fontSize:
                            9.5,
                          fontWeight:
                            600
                        }}
                      />


                      <PolarRadiusAxis
                        angle={30}
                        domain={[
                          0,
                          100
                        ]}
                        tick={
                          false
                        }
                        axisLine={
                          false
                        }
                      />


                      <Radar
                        name="Health"
                        dataKey="value"
                        stroke={
                          themeColor
                        }
                        strokeWidth={
                          2.1
                        }
                        fill={
                          themeColor
                        }
                        fillOpacity={
                          0.17
                        }
                        dot={{
                          r: 3.2,
                          fill:
                            themeColor,
                          stroke:
                            "var(--oz-surface)",
                          strokeWidth:
                            1.5
                        }}
                        animationDuration={
                          700
                        }
                      />


                      <RechartsTooltip
                        contentStyle={{
                          background:
                            "var(--oz-surface)",
                          border:
                            "1px solid var(--oz-border)",
                          borderRadius:
                            "9px",
                          color:
                            "var(--oz-text)",
                          fontSize:
                            "10px",
                          boxShadow:
                            "0 10px 30px rgba(0,0,0,.12)"
                        }}
                        formatter={(
                          value
                        ) => [
                          `${value}%`,
                          "Health"
                        ]}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>


                <div className="oz-inventory-quick-stats">
                  <div>
                    <span className="oz-mini-stat-icon themed">
                      <FiPackage />
                    </span>

                    <span>
                      <small>
                        Products
                      </small>

                      <strong>
                        {stats.totalProducts.toLocaleString(
                          "en-PK"
                        )}
                      </strong>
                    </span>
                  </div>


                  <div>
                    <span className="oz-mini-stat-icon red">
                      <FiAlertCircle />
                    </span>

                    <span>
                      <small>
                        Low Stock
                      </small>

                      <strong>
                        {stats.lowStockCount.toLocaleString(
                          "en-PK"
                        )}
                      </strong>
                    </span>
                  </div>


                  <div>
                    <span className="oz-mini-stat-icon blue">
                      <FaTruck />
                    </span>

                    <span>
                      <small>
                        Suppliers
                      </small>

                      <strong>
                        {stats.totalSuppliers.toLocaleString(
                          "en-PK"
                        )}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}


      {/* ===================================================
          CARDS VIEW
          NO LINE GRAPHS
      =================================================== */}

      {viewMode ===
        "cards" && (
        <div className="oz-summary-grid">

          <SummaryStatCard
            title={
              translations.todaysSales ||
              "Today's Sales"
            }
            value={
              stats.todaySales
            }
            icon={
              FaShoppingCart
            }
            themeColor={
              themeColor
            }
            isCurrency
            trend="+12%"
            subtitle={`${stats.todaySalesCount} invoices today`}
            onClick={() =>
              navigateToMenu(
                "sales",
                "saleslist"
              )
            }
          />


          <SummaryStatCard
            title="Total Purchases"
            value={stats.totalPurchases}
            icon={FaShoppingBag}
            themeColor={themeColor}
            isCurrency
            subtitle="Current month"
            onClick={() =>
              navigateToMenu(
                "purchases",
                "purchaselist"
              )
            }
          />


          <SummaryStatCard
            title="Total Paid"
            value={stats.totalPaid}
            icon={FaMoneyBillWave}
            themeColor={themeColor}
            isCurrency
            subtitle="Non-returned sales"
            onClick={() =>
              navigateToMenu(
                "sales",
                "saleslist"
              )
            }
          />


          <SummaryStatCard
            title="Total Dues"
            value={stats.totalDues}
            icon={FaFileInvoiceDollar}
            themeColor={themeColor}
            isCurrency
            danger={stats.totalDues > 0}
            subtitle="Outstanding non-returned sales"
            onClick={() =>
              navigateToMenu(
                "sales",
                "saleslist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.totalCustomers ||
              "Total Customers"
            }
            value={
              stats.totalCustomers
            }
            icon={
              FaUsers
            }
            themeColor={
              themeColor
            }
            trend="+5%"
            subtitle="Registered customers"
            onClick={() =>
              navigateToMenu(
                "customers",
                "customerlist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.suppliers ||
              "Suppliers"
            }
            value={
              stats.totalSuppliers
            }
            icon={
              FaTruck
            }
            themeColor={
              themeColor
            }
            subtitle="Active supplier network"
            onClick={() =>
              navigateToMenu(
                "suppliers",
                "supplierlist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.inventoryValue ||
              "Inventory Value"
            }
            value={
              stats.inventoryValue
            }
            icon={
              MdInventory
            }
            themeColor={
              themeColor
            }
            isCurrency
            trend="+18%"
            subtitle="Current stock valuation"
            onClick={() =>
              navigateToMenu(
                "stock",
                "stocklist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.products ||
              "Products"
            }
            value={
              stats.totalProducts
            }
            icon={
              FaBoxes
            }
            themeColor={
              themeColor
            }
            subtitle="Active inventory items"
            onClick={() =>
              navigateToMenu(
                "stock",
                "stocklist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.lowStockAlert ||
              "Low Stock Alert"
            }
            value={
              stats.lowStockCount
            }
            icon={
              FiAlertCircle
            }
            themeColor={
              themeColor
            }
            trend="Urgent"
            danger
            subtitle="Requires reorder"
            onClick={() =>
              navigateToMenu(
                "stock",
                "stocklist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.totalReturns ||
              "Total Returns"
            }
            value={
              moduleStats.totalReturns
            }
            icon={
              FaArrowCircleLeft
            }
            themeColor={
              themeColor
            }
            subtitle={`Amount ${formatPKR(
              moduleStats.totalReturnsAmount
            )}`}
            onClick={() =>
              navigateToMenu(
                "returns",
                "returnlist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.totalEmployees ||
              "Total Employees"
            }
            value={
              moduleStats.totalEmployees
            }
            icon={
              FaUserCog
            }
            themeColor={
              themeColor
            }
            subtitle={`${moduleStats.activeEmployees} active employees`}
            onClick={() =>
              navigateToMenu(
                "employees",
                "employeeslist"
              )
            }
          />
        </div>
      )}
    </div>
  );
};


export default CosmeticsDashboardHome;
